import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TabOptions } from '../../models/tab-options.model';
import { SelectDropdownOptions } from '../../models/select-dropdown-options.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { SelectComponent } from '../inputs/select/select.component';
import { IconComponent } from '../icon/icon.component';
import { Store } from '@ngxs/store';
import { ProfileState } from '../../state/states/profile/profile-state';
import { FormEditModeService } from '../../services/form-edit-mode.service';

@Directive({
  selector: '[workUiTabContent]',
  standalone: true,
})
export class TabContentDirective {
  template = inject<TemplateRef<never>>(TemplateRef);
  @Input() tabReference = '';
}

@Component({
  selector: 'work-ui-tabs',
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss'],
  standalone: true,
  imports: [IconComponent, SelectComponent, NgTemplateOutlet, TranslateModule],
})
export class TabsComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('tabGroup') tabGroup!: ElementRef;
  @ViewChildren('tabButton') tabButtons!: QueryList<ElementRef>;
  @ContentChildren(TabContentDirective) tabContentTemplates!: QueryList<TabContentDirective>;
  @Input() tabOptions: TabOptions[] = [];
  @Input() saveRouteInUrl = true;
  @Input() noPadding = false;
  @Input() contentPadding = true;
  @Input() cardTabs = false;
  @Input() activeTab!: string; // Ensure the activeTab is managed as an input
  @Input() routeParamName: 'tab' | 'subtab' = 'tab';
  // Tabs, die nur Abschnitte EINES Formulars gruppieren (alle Panels bleiben im DOM), dürfen beim
  // Tabwechsel nicht nach ungespeicherten Änderungen fragen — dort geht durch den Wechsel nichts verloren.
  @Input() checkUnsavedChangesOnTabChange = true;
  @Output() activeTabChange: EventEmitter<string> = new EventEmitter<string>();
  public tabsDoOverflow = signal(false);
  public selectOptions: SelectDropdownOptions[] = [];
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private store = inject(Store);
  private formEditModeService = inject(FormEditModeService);
  // Needed because the activeTab write happens inside a setTimeout (originally a
  // workaround for NG0100). When this component is hosted inside an OnPush ancestor,
  // the post-setTimeout plain-field write doesn't propagate — `markForCheck` walks
  // up the tree so the next tick re-renders the active-tab styles.
  private changeDetectorRef = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  private tabGroupResizeObserver!: ResizeObserver;
  private routingChangeSubscription: Subscription | undefined;

  constructor() {
    afterNextRender(() => {
      this.setupResizeObserver();
    });
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe((params) => {
      this.updateActiveTabFromParams(params);
    });

    this.tabOptions = this.tabOptions.map((tabOption) => {
      if (tabOption.requiredPermission && !this.store.selectSnapshot(ProfileState.hasPermission(tabOption.requiredPermission))) {
        tabOption.hidden = true;
      }
      return tabOption;
    });

    this.populateSelectOptions();

    this.routingChangeSubscription = this.router.events.subscribe((event) => {
      // Only sync the active tab from the URL when this tabs instance actually persists itself there.
      // With `saveRouteInUrl = false` the tab is purely internal, so an unrelated navigation (e.g. the
      // montageplan calendar updating its `?date` query param while scrolling) must not reset it to the
      // first tab — `updateActiveTabFromParams` would otherwise do exactly that, since no tab param exists.
      if (event instanceof NavigationEnd && this.saveRouteInUrl) {
        this.updateActiveTabFromParams(this.activatedRoute.snapshot.params);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tabOptions'] && changes['tabOptions'].currentValue) {
      this.populateSelectOptions();
      this.updateActiveTabFromParams(this.activatedRoute.snapshot.params);
    }
  }

  ngOnDestroy() {
    this.tabGroupResizeObserver?.disconnect();
    if (this.routingChangeSubscription) {
      this.routingChangeSubscription.unsubscribe();
    }
  }

  /**
   * **************************************
   * *** DANGER ** THIS METHOD IS HACKY ***
   * **************************************
   *
   *
   * @param tabReference
   * @param preventUrlChange
   */
  public activateTab(tabReference: string, preventUrlChange = false) {
    // prevent changeDetection errors in the parent by wrapping this in setTimeout
    setTimeout(async () => {
      if (this.activeTab !== tabReference) {
        // check if formEditMode has unsaved changes
        if (this.checkUnsavedChangesOnTabChange) {
          const canProceed = await this.formEditModeService.checkUnsavedChanges();
          if (!canProceed) {
            return; // Don't switch tabs if user cancels
          }
        }
        this.activeTab = tabReference;
        this.activeTabChange.emit(tabReference);
        this.changeDetectorRef.markForCheck();
      }
    });

    if (!preventUrlChange && this.saveRouteInUrl) {
      let route = this.activatedRoute.snapshot;
      let hasAuxiliaryOutlet = false;
      const urlSegmentsPerOutlet: { [outlet: string]: string[] } = {};

      while (route.parent) {
        route = route.parent;
        if (route.url.length) {
          const outlet = this.getOutletForRoute(route);
          if (outlet !== 'primary') {
            hasAuxiliaryOutlet = true;
          }
          if (!urlSegmentsPerOutlet[outlet]) {
            urlSegmentsPerOutlet[outlet] = [];
          }
          urlSegmentsPerOutlet[outlet] = [...route.url.map((segment) => segment.path), ...urlSegmentsPerOutlet[outlet]];
        }
      }

      // Get the current URL params for tab/subtab, remove subtab if we're changing tab.
      const currentParams = { ...this.activatedRoute.snapshot.params };
      if (this.routeParamName === 'tab') {
        delete currentParams['subtab'];
      }

      // Update the specific parameter
      currentParams[this.routeParamName] = tabReference;

      // Store primary outlet in seperate variable and delete it.
      const primaryOutletSegments = urlSegmentsPerOutlet['primary'];
      for (const [outlet, segments] of Object.entries(urlSegmentsPerOutlet)) {
        const currentParamsArray = Object.values(currentParams)
          .filter((value: string) => segments.indexOf(value) === -1)
          .map((value: string) => value) as string[];
        urlSegmentsPerOutlet[outlet] = [...segments, ...currentParamsArray];
      }

      if (hasAuxiliaryOutlet) {
        delete urlSegmentsPerOutlet['primary'];
        this.router
          .navigate([...primaryOutletSegments, { outlets: urlSegmentsPerOutlet }], {
            queryParamsHandling: 'merge',
            queryParams: this.activatedRoute.snapshot.queryParams,
          })
          .catch();
      } else {
        this.router
          .navigate([...urlSegmentsPerOutlet['primary']], {
            queryParamsHandling: 'merge',
            queryParams: this.activatedRoute.snapshot.queryParams,
          })
          .catch();
      }
    }
  }

  public onKeydown(event: KeyboardEvent) {
    const activeTabIndex = this.tabOptions.findIndex((tab) => tab.tabReference === this.activeTab);
    if (event.key === 'ArrowRight') {
      const nextTabReference = this.tabOptions[(activeTabIndex + 1) % this.tabOptions.length].tabReference;
      this.activateTab(nextTabReference);
    } else if (event.key === 'ArrowLeft') {
      const previousTabReference = this.tabOptions[activeTabIndex === 0 ? this.tabOptions.length - 1 : activeTabIndex - 1].tabReference;
      this.activateTab(previousTabReference);
    }
  }

  private setupResizeObserver() {
    if (!this.tabGroup?.nativeElement) return;

    this.tabGroupResizeObserver = new ResizeObserver(() => {
      const hasOverflow = this.tabGroupHasOverflow();
      this.tabsDoOverflow.set(hasOverflow);
    });

    this.tabGroupResizeObserver.observe(this.tabGroup.nativeElement);
  }

  private updateActiveTabFromParams(params: Params) {
    if (this.tabOptions.length === 0) {
      return;
    }
    // Without `saveRouteInUrl` the URL holds no tab param, so only fall back to the first tab when the active one
    // no longer exists — otherwise every `tabOptions` update (e.g. refreshed badge counts) resets the user's choice.
    if (!this.saveRouteInUrl) {
      if (!this.tabOptions.some((tab) => tab.tabReference === this.activeTab)) {
        this.activateTab(this.tabOptions[0].tabReference, true);
      }
      return;
    }
    const tabKey = params[this.routeParamName];
    if (tabKey && this.tabOptions.some((tab) => tab.tabReference === tabKey)) {
      this.activateTab(tabKey, true);
    } else if (!tabKey) {
      this.activateTab(this.tabOptions[0].tabReference, true);
    }
  }

  private populateSelectOptions() {
    this.selectOptions = [];
    this.tabOptions.forEach((tab) => {
      const title = tab.title ? this.translateService.instant(tab.title) : '';
      this.selectOptions.push({
        label: `${title}${tab.badgeValue && +tab.badgeValue > 0 ? ` (${tab.badgeValue})` : ''}`,
        value: tab.tabReference,
      });
    });
  }

  private tabGroupHasOverflow() {
    const element = this.tabGroup.nativeElement;
    return element.scrollWidth > element.clientWidth;
  }

  private getOutletForRoute(route: ActivatedRouteSnapshot): string {
    let currentRoute: ActivatedRouteSnapshot | null = route;

    while (currentRoute?.parent) {
      if (currentRoute.outlet !== 'primary') {
        return currentRoute.outlet; // Found the named outlet
      }
      currentRoute = currentRoute.parent; // Move up
    }

    return 'primary'; // Default to primary if no named outlet is found
  }
}
